import React from 'react'
import styled from 'styled-components/macro'
import { StockQuietButton } from 'src/components/stock/StockPageChrome'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  WASTAGE_COPY,
  WASTAGE_REASON_CHIPS,
  wastageEntryDateLabel,
  wastageFilterCostLabel,
  wastageFilterQuantityLabel,
  wastageStockTakePeriodLabel,
} from 'src/constants/wastage'

export const wastageFilterOptionValue = option =>
  option?.kind === 'recipe' ? `recipe:${option.id}` : String(option?.id || '')

export const wastageProductOptions = (entries = []) => {
  const seen = new Set()
  const options = []
  for (const entry of entries) {
    if (entry?.deletedAt) continue
    const recipe =
      entry?.kind === 'recipe' || (!entry?.venueProductId && entry?.recipeId)
    const id = recipe
      ? String(entry?.recipeId || '')
      : String(entry?.venueProductId || '')
    if (!id) continue
    const key = `${recipe ? 'recipe' : 'product'}:${id}`
    if (seen.has(key)) continue
    seen.add(key)
    options.push({
      id,
      kind: recipe ? 'recipe' : 'product',
      title: entry.title || (recipe ? 'Recipe' : 'Product'),
      supplier: recipe ? '' : String(entry.supplierTitle || '').trim(),
    })
  }
  return options
}

const optionLabel = option => {
  if (option?.kind === 'recipe') {
    return `${option.title} · ${WASTAGE_COPY.recipe}`
  }
  return option.supplier ? `${option.title} · ${option.supplier}` : option.title
}

export const WasteFilters = ({
  today,
  month,
  range,
  onRange,
  product,
  onProduct,
  options,
  reason = '',
  onReason,
  userId = '',
  onUser,
  userOptions = [],
  totals,
  invalidRange,
  onDownload,
  downloading,
  downloadError,
  showRemoved = false,
  onShowRemoved,
}) => {
  const productId = wastageFilterOptionValue(product)
  const isToday = range.from === today && range.to === today
  const isMonth = range.from === month.from && range.to === month.to
  const rangeLabel = isToday
    ? WASTAGE_COPY.dateToday
    : range.from === range.to
    ? wastageEntryDateLabel(range.from, today)
    : wastageStockTakePeriodLabel(range.from, range.to)
  const costLabel = invalidRange
    ? null
    : wastageFilterCostLabel(totals?.costValuePence)
  const quantityLabel =
    !invalidRange && productId
      ? wastageFilterQuantityLabel(totals?.quantity)
      : null
  const selected =
    product && !options.some(option => option.id === product.id)
      ? product
      : null

  return (
    <Bar data-testid='waste-filters'>
      <Chips>
        <Chip
          type='button'
          data-testid='waste-month-chip'
          aria-pressed={isMonth}
          onClick={() => onRange(month)}
        >
          {wastageStockTakePeriodLabel(month.from, month.to)}
        </Chip>
        <Chip
          type='button'
          data-testid='waste-today-chip'
          aria-pressed={isToday}
          onClick={() => onRange({ from: today, to: today })}
        >
          {WASTAGE_COPY.dateToday}
        </Chip>
        <Chip
          type='button'
          data-testid='waste-show-removed'
          aria-pressed={showRemoved}
          onClick={() => onShowRemoved && onShowRemoved(!showRemoved)}
        >
          {WASTAGE_COPY.showRemoved}
        </Chip>
      </Chips>
      <Dates>
        <DateInput
          type='date'
          data-testid='waste-from'
          value={range.from}
          max={range.to || undefined}
          onChange={event => {
            if (!event.target.value) return
            onRange({ from: event.target.value, to: range.to })
          }}
        />
        <DateInput
          type='date'
          data-testid='waste-to'
          value={range.to}
          min={range.from || undefined}
          onChange={event => {
            if (!event.target.value) return
            onRange({ from: range.from, to: event.target.value })
          }}
        />
      </Dates>
      <ProductSelect
        data-testid='waste-product-filter'
        value={productId || ''}
        onChange={event => onProduct(event.target.value)}
      >
        <option value=''>All products and recipes</option>
        {selected &&
        !options.some(
          option =>
            wastageFilterOptionValue(option) ===
            wastageFilterOptionValue(selected),
        ) ? (
          <option value={wastageFilterOptionValue(selected)}>
            {optionLabel(selected)}
          </option>
        ) : null}
        {options.map(option => (
          <option
            key={wastageFilterOptionValue(option)}
            value={wastageFilterOptionValue(option)}
          >
            {optionLabel(option)}
          </option>
        ))}
      </ProductSelect>
      <ProductSelect
        data-testid='waste-reason-filter'
        value={reason || ''}
        onChange={event => onReason && onReason(event.target.value)}
      >
        <option value=''>{WASTAGE_COPY.filterAllReasons}</option>
        {WASTAGE_REASON_CHIPS.map(chip => (
          <option key={chip.id} value={chip.id}>
            {chip.label}
          </option>
        ))}
      </ProductSelect>
      <ProductSelect
        data-testid='waste-user-filter'
        value={userId || ''}
        onChange={event => onUser && onUser(event.target.value)}
      >
        <option value=''>{WASTAGE_COPY.filterAllPeople}</option>
        {userOptions.map(person => (
          <option key={person.id} value={person.id}>
            {person.name}
          </option>
        ))}
      </ProductSelect>
      <StockQuietButton
        type='button'
        data-testid='waste-csv'
        onClick={onDownload}
        disabled={downloading || invalidRange}
      >
        Download CSV
      </StockQuietButton>
      {downloadError ? (
        <DownloadError data-testid='waste-csv-error'>
          {downloadError}
        </DownloadError>
      ) : null}
      {invalidRange ? (
        <DownloadError data-testid='waste-range-error'>
          {WASTAGE_COPY.reversedRange}
        </DownloadError>
      ) : null}
      {costLabel || quantityLabel ? (
        <Totals data-testid='waste-filter-totals'>
          {costLabel ? (
            <Cost>
              {costLabel}
              {rangeLabel ? <Range> · {rangeLabel}</Range> : null}
            </Cost>
          ) : null}
          {quantityLabel ? (
            <Quantity data-testid='waste-filter-quantity'>
              {quantityLabel}
            </Quantity>
          ) : null}
        </Totals>
      ) : null}
    </Bar>
  )
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.button`
  margin: 0;
  padding: 0.4rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  }
`

const Dates = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const DateInput = styled.input`
  margin: 0;
  padding: 0.35rem 0.5rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-size: 0.9rem;
`

const ProductSelect = styled.select`
  margin: 0;
  padding: 0.4rem 0.5rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-size: 0.9rem;
`

const Totals = styled.div`
  flex-basis: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
`

const Cost = styled.div``

const Range = styled.span`
  font-weight: 500;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Quantity = styled.div``

const DownloadError = styled.div`
  flex-basis: 100%;
  color: ${({ theme }) => theme.colours.chilliRed || '#b42318'};
  font-size: 0.9rem;
  font-weight: 600;
`
